import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import SiteFooter from "./SiteFooter";

const normalizeDemoKey = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const demoThumbnailContext = require.context("../demo", false, /\.png$/i);
const demoThumbnails = demoThumbnailContext.keys().reduce((map, key) => {
  const fileName = key.replace("./", "").replace(/\.png$/i, "");
  map[normalizeDemoKey(fileName)] = demoThumbnailContext(key);
  return map;
}, {});

const getDemoThumbnail = (team) => {
  const keys = [
    team.demoThumbnail,
    team.thumbnail,
    team.name,
    `Team ${team.name}`,
    `Team${team.name}`,
  ];
  return keys
    .map(normalizeDemoKey)
    .map((key) => demoThumbnails[key])
    .find(Boolean);
};

/* ─── Animated Counter ───────────────────────────────────────────────────────── */
function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState("0");
  useEffect(() => {
    if (!start) return;
    const num = parseInt(target.replace(/\D/g, ""), 10);
    const suffix = target.replace(/[\d]/g, "");
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num) + suffix);
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedStat({ number, label, started }) {
  const val = useCounter(number, 1600, started);
  return (
    <div className="hero-stat">
      <div className="hero-stat-number">{val}</div>
      <div className="hero-stat-label">{label}</div>
    </div>
  );
}

/* ─── Terminal ───────────────────────────────────────────────────────────────── */
const TERMINAL_LINES = [
  { prefix: "$ ", text: "quantum init my-project",          color: "#4f8ef7", delay: 400 },
  { prefix: "  ", text: "✓ Project scaffold created",       color: "#22c55e", delay: 900 },
  { prefix: "  ", text: "✓ Quantum runtime installed",      color: "#22c55e", delay: 1300 },
  { prefix: "$ ", text: "quantum run --watch",              color: "#4f8ef7", delay: 1900 },
  { prefix: "  ", text: "⚡ Server running on :3000",       color: "#f59e0b", delay: 2400 },
  { prefix: "  ", text: "◉ Hot reload active",              color: "#a78bfa", delay: 2900 },
  { prefix: "$ ", text: "quantum meet --start",             color: "#4f8ef7", delay: 3500 },
  { prefix: "  ", text: "🎥 QuantumMeet session started",   color: "#22c55e", delay: 4000 },
  { prefix: "$ ", text: "docssyncer sync --all",            color: "#4f8ef7", delay: 4600 },
  { prefix: "  ", text: "✓ Docs synced in 0.3s",            color: "#22c55e", delay: 5100 },
  { prefix: "  ", text: "▌",                                color: "#4f8ef7", delay: 5600 },
];

function Terminal() {
  const [lines, setLines] = useState([]);
  const [ref, inView] = useInView(0.3);
  const started = useRef(false);
  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    TERMINAL_LINES.forEach((l) => setTimeout(() => setLines((p) => [...p, l]), l.delay));
  }, [inView]);
  return (
    <div className="terminal-wrap" ref={ref}>
      <div className="terminal-bar">
        <span className="t-dot t-red" /><span className="t-dot t-yellow" /><span className="t-dot t-green" />
        <span className="t-title">quantum — bash</span>
      </div>
      <div className="terminal-body">
        {lines.map((l, i) => (
          <div key={i} className="terminal-line">
            <span className="t-prefix" style={{ color: l.color }}>{l.prefix}</span>
            <span className="t-text">{l.text}</span>
          </div>
        ))}
        {lines.length === 0 && (
          <div className="terminal-line">
            <span className="t-prefix" style={{ color: "#4f8ef7" }}>$ </span>
            <span className="t-cursor">▌</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Tech Marquee ───────────────────────────────────────────────────────────── */
const STACK = ["React","Node.js","TypeScript","MongoDB","Express","Docker","Kubernetes","AWS","Redis","GraphQL","WebRTC","Rust","Python","PostgreSQL","Nginx","Terraform"];
function TechMarquee() {
  const doubled = [...STACK, ...STACK];
  return (
    <div className="marquee-track">
      <div className="marquee-inner">
        {doubled.map((t, i) => (
          <span key={i} className="marquee-item"><span className="marquee-dot" />{t}</span>
        ))}
      </div>
    </div>
  );
}

/* ─── Feature Tabs ───────────────────────────────────────────────────────────── */
const FEATURES = [
  {
    id: "speed", label: "Lightning Fast",
    icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>),
    heading: "Built for Speed at Every Layer",
    body: "From our custom-compiled Quantum Language runtime to our zero-latency QuantumMeet engine, we obsess over performance. Our systems handle millions of operations per second with sub-millisecond response times.",
    code: `// Quantum Language - native speed\nfn process_stream(data: Stream<Bytes>) -> Result {\n  data\n    |> filter(|chunk| chunk.len() > 0)\n    |> map(compress)          // parallel\n    |> batch(1024)\n    |> sink(output_channel)   // zero-copy\n}\n// Compiles to native — no VM overhead`,
    file: "stream.sa",
  },
  {
    id: "scale", label: "Infinite Scale",
    icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>),
    heading: "Scale to Millions Effortlessly",
    body: "Our architecture is designed to scale horizontally from day one. Kubernetes-native, auto-scaling, and distributed by default — deploy to any cloud and grow without rearchitecting.",
    code: `# quantum.config.yaml\nscale:\n  replicas: auto\n  min: 2\n  max: 500\n  metric: rps\n  target: 10_000\n\ndeploy:\n  strategy: rolling\n  canary: true\n  rollback: auto`,
    file: "quantum.config.yaml",
  },
  {
    id: "secure", label: "Secure by Default",
    icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
    heading: "Security Baked Into the Stack",
    body: "End-to-end encryption across all products. QuantumMeet uses DTLS-SRTP for media. The Quantum compiler catches security vulnerabilities at compile time, not runtime.",
    code: `// Security at the type level\nlet conn: Encrypted<TlsStream> =\n  TlsStream::connect(addr)?;\n\n// Compile-time capability check\n@requires(Permission::Admin)\nfn delete_user(id: UserId) -> Result {\n  // compiler enforces auth\n}`,
    file: "auth.sa",
  },
  {
    id: "dx", label: "Developer Experience",
    icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>),
    heading: "DX That Developers Love",
    body: "Hot reload, instant error messages with exact line numbers, integrated type system, and a package ecosystem that just works. DocsSyncer keeps every doc always current.",
    code: `$ quantum check\n✓ Type check passed (342 files)\n✓ No security vulnerabilities\n✓ Bundle size: 14.2kb gzipped\n\nError in src/api/users.sa:47\n  Type mismatch: expected String\n  found Option<String>\n\n  Hint: use .unwrap_or("") to fix`,
    file: "terminal",
  },
];

function FeatureTabs() {
  const [active, setActive] = useState(0);
  const feat = FEATURES[active];
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`features-section${inView ? " in-view" : ""}`}>
      <div className="features-tabs">
        {FEATURES.map((f, i) => (
          <button key={f.id} className={`feat-tab${active === i ? " active" : ""}`} onClick={() => setActive(i)}>
            <span className="feat-tab-icon">{f.icon}</span>{f.label}
          </button>
        ))}
      </div>
      <div className="features-panel">
        <div className="features-panel-text">
          <h3 className="features-panel-heading">{feat.heading}</h3>
          <p className="features-panel-body">{feat.body}</p>
          <div className="features-panel-checks">
            {["Sub-ms latency", "99.99% uptime SLA", "Open source core"].map((c) => (
              <div key={c} className="feat-check">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {c}
              </div>
            ))}
          </div>
        </div>
        <div className="features-panel-code">
          <div className="code-file-bar">
            <span className="code-file-dot" />
            <span className="code-file-name">{feat.file}</span>
          </div>
          <pre className="code-block"><code>{feat.code}</code></pre>
        </div>
      </div>
    </div>
  );
}

/* ─── Testimonials ───────────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  { quote: "Quantum Language cut our backend build time in half. The type system catches bugs we used to find in production.", name: "Ahmed Raza",   role: "CTO, Nexaflow",        init: "AR", color: "#4f8ef7" },
  { quote: "QuantumMeet replaced our entire conferencing stack. Crystal-clear video, zero config, and it just works behind any firewall.", name: "Maria Khalid", role: "Head of Eng, Pakdev",  init: "MK", color: "#a78bfa" },
  { quote: "DocsSyncer is the tool we didn't know we needed. Our API docs are always accurate now — automatically.", name: "Usman Tariq", role: "Lead Dev, TechBridge PK", init: "UT", color: "#22c55e" },
];

/* ─── Main ───────────────────────────────────────────────────────────────────── */
export default function HomePage({ setShowAuth, currentUser }) {
  const navigate = useNavigate();
  const [statsRef, statsInView] = useInView(0.3);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const dashboardPath =
    currentUser?.role === "admin"
      ? "/admin-panel"
      : currentUser?.role === "interviewer"
        ? "/interviewer-panel"
        : "/dashboard";

  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial((p) => (p + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const products = [
    { logo: "/quantum.png",     alt: "Quantum Language", badge: "Open Source", badgeClass: "badge-blue",   eyebrow: "Programming Language", name: "Quantum",     desc: "A purpose-built language designed from the ground up — optimized for speed, readability, and modern workflows.",                                      url: "https://quantum.quantumlogicslimited.com/",     urlLabel: "quantum.quantumlogicslimited.com",     productGlow: "rgba(79,142,247,0.12)",   features: ["Static typing","Native speed","Modern syntax"] },
    { logo: "/quantummeet.png", alt: "QuantumMeet",      badge: "Live",        badgeClass: "badge-violet", eyebrow: "Video Conferencing",    name: "QuantumMeet", desc: "High-performance video conferencing built in-house. Crystal-clear quality, E2E encryption, and real-time collaboration.",                           url: "https://meet.quantumlogicslimited.com/",        urlLabel: "meet.quantumlogicslimited.com",        productGlow: "rgba(167,139,250,0.12)",  features: ["E2E encrypted","HD video","Screen share"] },
    { logo: "/docssyncer.png",  alt: "DocsSyncer",       badge: "AI Powered",  badgeClass: "badge-amber",  eyebrow: "Documentation",         name: "DocsSyncer",  desc: "An intelligent documentation engine that keeps Quantum language docs always current — syncing, validating, and publishing automatically.", url: "https://docssyncer.quantumlogicslimited.com/", urlLabel: "docssyncer.quantumlogicslimited.com", productGlow: "rgba(245,158,11,0.12)",   features: ["AI-powered","Auto-sync","Version aware"] },
  ];

  const values = [
    { icon: "⚡", title: "Hard Work",  desc: "Relentless effort in every line of code, every pixel, every decision." },
    { icon: "🤝", title: "Teamwork",   desc: "We build, solve, and grow together — sharing knowledge at every level." },
    { icon: "🎯", title: "Dedication", desc: "We see projects through with unwavering focus on quality." },
    { icon: "🚀", title: "Innovation", desc: "We challenge convention and embrace emerging technologies." },
    { icon: "🛡️", title: "Integrity", desc: "Transparency and accountability in every relationship." },
    { icon: "📐", title: "Precision",  desc: "Measurable goals, rigorous tracking, outcomes that matter." },
  ];

  const team = [
    { name: "Zaid Akhtar",  role: "CEO & Co-Founder", init: "ZA", color: "#4f8ef7" },
    { name: "Sara Mehmood", role: "CTO",              init: "SM", color: "#a78bfa" },
    { name: "Hassan Ali",   role: "Lead Engineer",    init: "HA", color: "#22c55e" },
    { name: "Nadia Khan",   role: "Head of Design",   init: "NK", color: "#f59e0b" },
  ];

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-glow-top" />
        <div className="hero-grid" />
        <div className="hero-inner">
          <div className="fade-up">
            <a href="https://quantum.quantumlogicslimited.com/" target="_blank" rel="noopener noreferrer" className="hero-announce">
              <span className="hero-announce-badge">New</span>
              Quantum Language 2.0 is here — faster compiler, better DX
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </a>
          </div>
          <h1 className="hero-h1 fade-up-1">
            We Build the Tools<br /><em>Developers Actually Want.</em>
          </h1>
          <p className="hero-sub fade-up-2">
            Quantum Logics is a software house that ships its own language, its own meeting platform, and its own AI documentation engine — while building world-class products for clients.
          </p>
          {currentUser ? (
            <div className="hero-actions fade-up-3">
              <button className="btn btn-primary" onClick={() => navigate(dashboardPath)}>
                Go to Dashboard
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          ) : (
            <div className="hero-actions fade-up-3">
              <button className="btn btn-primary" onClick={() => navigate("/jobs")}>
                Join Our Team
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAuth("register")}>Create Account</button>
            </div>
          )}
          <div className="hero-products-strip fade-up-3">
            <span className="hero-products-label">Our products →</span>
            {[
              { src:"/quantum.png",     href:"https://quantum.quantumlogicslimited.com/",  label:"Quantum" },
              { src:"/quantummeet.png", href:"https://meet.quantumlogicslimited.com/",     label:"QuantumMeet" },
              { src:"/docssyncer.png",  href:"https://docssyncer.quantumlogicslimited.com/",label:"DocsSyncer" },
            ].map((p) => (
              <a key={p.href} href={p.href} target="_blank" rel="noopener noreferrer" className="hero-product-chip" title={p.label}>
                <img src={p.src} alt={p.label} width="20" height="20" />
                <span>{p.label}</span>
              </a>
            ))}
          </div>
        </div>
        <div className="hero-terminal-wrap fade-up-2"><Terminal /></div>
      </section>

      {/* STATS STRIP */}
      <div className="stats-strip" ref={statsRef}>
        {[["120+","Projects Delivered"],["45+","Team Members"],["6yr","In Business"],["98%","Client Satisfaction"],["3","Proprietary Products"],["∞","Lines of Quantum Code"]].map(([num, label], i) => (
          <div key={i} className="stats-strip-item">
            <div className="stats-strip-number">
              {num === "∞" || num === "3" ? num : <AnimatedStat number={num} label="" started={statsInView} />}
            </div>
            <div className="stats-strip-label">{label}</div>
          </div>
        ))}
      </div>

      {/* MARQUEE */}
      <div className="marquee-section">
        <span className="marquee-prefix">Powered by</span>
        <TechMarquee />
      </div>

      {/* ECOSYSTEM */}
      <section className="section section-alt" id="products">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-label">Our Ecosystem</span>
              <h2 className="section-heading">Built In-House.<br /><em>Shipped to the World.</em></h2>
            </div>
            <p className="section-sub">Beyond client work, our engineers ship their own tools — a full stack of proprietary software used by developers everywhere.</p>
          </div>
          <div className="products-grid">
            {products.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="product-card" style={{ "--product-glow": p.productGlow }}>
                <div className="product-card-header">
                  <img src={p.logo} alt={p.alt} className="product-logo" width="52" height="52" />
                  <span className={`product-badge ${p.badgeClass}`}>{p.badge}</span>
                </div>
                <p className="product-eyebrow">{p.eyebrow}</p>
                <h3 className="product-name">{p.name}</h3>
                <p className="product-desc">{p.desc}</p>
                <ul className="product-features-list">
                  {p.features.map((f) => (
                    <li key={f} className="product-feature-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="product-footer">
                  <span className="product-url">{p.urlLabel}</span>
                  <span className="product-arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" id="features">
        <div className="container">
          <span className="section-label">Engineering Excellence</span>
          <h2 className="section-heading">Why Teams Choose<br /><em>Quantum Logics.</em></h2>
          <p className="section-sub" style={{ marginBottom: "3rem" }}>We build with speed, security, and scale at the core — not bolted on after the fact.</p>
          <FeatureTabs />
        </div>
      </section>

      {/* PROCESS */}
      <section className="section section-alt" id="process">
        <div className="container">
          <span className="section-label">How We Work</span>
          <h2 className="section-heading">From Idea to<br /><em>Production in Weeks.</em></h2>
          <p className="section-sub" style={{ marginBottom: "3rem" }}>A battle-tested process refined across 120+ projects.</p>
          <div className="process-steps">
            {[
              { num:"01", title:"Discovery",    desc:"Deep-dive into your problem space. Map requirements, define scope, align on success metrics.",                             icon:"🔍" },
              { num:"02", title:"Architecture", desc:"System design, tech stack selection, proof-of-concept. We catch hard problems early.",                                    icon:"📐" },
              { num:"03", title:"Build",        desc:"Iterative sprints with daily updates. Quantum Language + modern stack. You see progress every day.",                      icon:"⚡" },
              { num:"04", title:"Ship & Scale", desc:"CI/CD pipelines, monitoring, auto-scaling. We don't just deploy — we make sure it performs.",                            icon:"🚀" },
            ].map((s, i) => (
              <div key={i} className="process-step">
                <div className="process-step-num">{s.num}</div>
                <div className="process-step-icon">{s.icon}</div>
                <h4 className="process-step-title">{s.title}</h4>
                <p className="process-step-desc">{s.desc}</p>
                {i < 3 && <div className="process-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section testimonials-section">
        <div className="container">
          <span className="section-label">Testimonials</span>
          <h2 className="section-heading">Loved by<br /><em>Engineering Teams.</em></h2>
          <div className="testimonials-row">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`testimonial-card${activeTestimonial === i ? " active" : ""}`} onClick={() => setActiveTestimonial(i)}>
                <div className="testimonial-quote-icon">"</div>
                <p className="testimonial-text">{t.quote}</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar" style={{ background: `linear-gradient(135deg,${t.color}88,${t.color})` }}>{t.init}</div>
                  <div><div className="testimonial-name">{t.name}</div><div className="testimonial-role">{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
          <div className="testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button key={i} className={`testimonial-dot${activeTestimonial === i ? " active" : ""}`} onClick={() => setActiveTestimonial(i)} />
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-label">Core Values</span>
              <h2 className="section-heading">The Code We<br /><em>Live By.</em></h2>
            </div>
            <p className="section-sub">Every team member lives by these values — the engine of everything we build.</p>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className="value-item">
                <span className="value-icon">{v.icon}</span>
                <h4 className="value-title">{v.title}</h4>
                <p className="value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section">
        <div className="container">
          <span className="section-label">Leadership</span>
          <h2 className="section-heading">The Minds<br /><em>Behind Quantum.</em></h2>
          <p className="section-sub" style={{ marginBottom: "3rem" }}>Engineers and builders who ship real products to real users.</p>
          <div className="team-grid">
            {team.map((t, i) => {
              const thumbnail = getDemoThumbnail(t);
              return (
                <div key={i} className="team-card">
                  {thumbnail ? (
                    <img
                      className="team-thumbnail"
                      src={thumbnail}
                      alt={`${t.name} demo thumbnail`}
                    />
                  ) : (
                    <div className="team-avatar" style={{ background: `linear-gradient(135deg,${t.color}55 0%,${t.color} 100%)` }}>{t.init}</div>
                  )}
                  <div className="team-name">{t.name}</div>
                  <div className="team-role">{t.role}</div>
                  <div className="team-tag">Engineering</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="cta-glow" />
        <div className="container cta-inner">
          <div className="cta-grid-bg" />
          <div className="cta-content">
            <span className="section-label">We're Hiring</span>
            <h2 className="section-heading" style={{ fontSize: "clamp(1.75rem,4vw,3.25rem)" }}>Build the Future<br /><em>With Us.</em></h2>
            <p className="section-sub cta-sub">Remote-friendly, high-trust, high-output. We hire for talent, drive, and values.</p>
            {currentUser ? (
              <div className="hero-actions" style={{ justifyContent: "center" }}>
                <button className="btn btn-primary" onClick={() => navigate(dashboardPath)}>
                  Go to Dashboard
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            ) : (
              <div className="hero-actions" style={{ justifyContent: "center" }}>
                <button className="btn btn-primary" onClick={() => navigate("/jobs")}>
                  View Open Roles
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                <button className="btn btn-secondary" onClick={() => setShowAuth("register")}>Create Account</button>
              </div>
            )}
            <div className="cta-perks">
              {["Remote-friendly","Equity available","Latest tech stack","Strong eng culture"].map((p) => (
                <span key={p} className="cta-perk">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </>
  );
}
